import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { KstDataGrid, type KstDataGridColumn, type KstDataGridRows, type KstDataGridHeaderButton } from '@/components/KstDataGrid';
import { ConfirmModal } from '@/components/ConfirmModal';
import { AddVehicleModal } from '@/components/modals/AddVehicleModal';
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import { hasValue } from '@/utils/legacy-utils';

// Lazy wrappers: these window globals are set by MyBuiltScripts.js which loads
// after ReactBundle.js. Wrapping them defers resolution to call time.
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const deepCopyProperties = _wg('deepCopyProperties');

const vehicleColumns: KstDataGridColumn[] = [
  { field: 'id', name: 'Id', size: 'hidden' },
  {
    field: 'vehicleName',
    name: 'Assigned Vehicle Name',
    size: 'col-xs-10',
    template: (e: any) => {
      if (hasValue(e) && hasValue(e.vehicleName)) {
        return e.vehicleName;
      }
      return 'Error getting Assigned Vehicle.';
    },
  },
];

const vehicleHeaderButtons: KstDataGridHeaderButton[] = [
  { name: 'Add', function: 'add', src: '/Content/TourImages/add-r.svg' },
  { name: 'Delete', function: 'delete', src: '/Content/TourImages/trash.svg' },
];

export function VehicleManagementScreen() {
  const { theme } = useTheme();

  const [vehicleEntries, setVehicleEntries] = useState<any[] | null>(null);
  const [isVehiclesLoading, setIsVehiclesLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const vehicleListCacheRef = useRef<any>(null);

  // Add Vehicle modal
  const [addModalOpen, setAddModalOpen] = useState(false);

  // Delete confirm modal
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmItems, setDeleteConfirmItems] = useState<any[]>([]);
  const [deleteConfirmMessage, setDeleteConfirmMessage] = useState('');

  // Grid ref for scroll
  const gridContainerRef = useRef<HTMLDivElement>(null);

  const vehicleRows: KstDataGridRows = useMemo(
    () => ({ entries: vehicleEntries }),
    [vehicleEntries]
  );

  const clearCachedValues = useCallback(() => {
    vehicleListCacheRef.current = null;
  }, []);

  const getVehicleListPromise = useCallback((): Promise<any> => {
    return new Promise((resolve, reject) => {
      if (hasValue(vehicleListCacheRef.current)) {
        resolve(deepCopyProperties(vehicleListCacheRef.current));
      } else {
        api.listCompanyVehicles(true, false, true, (result: any) => {
          vehicleListCacheRef.current = result;
          resolve(deepCopyProperties(result));
        }, (errorResult: any) => {
          reject(errorResult);
          App.showPopupNotification('Error getting Assigned Vehicle.', 'error');
        });
      }
    });
  }, []);

  const refreshVehicleList = useCallback((doScrollToBottom = false) => {
    setIsVehiclesLoading(true);
    getVehicleListPromise().then((listResponse: any) => {
      const vehicles = listResponse.companyVehicles || [];
      for (let i = 0; i < vehicles.length; i++) {
        vehicles[i].listIdx = i + 1;
        vehicles[i].isListEnabled = true;
      }
      setVehicleEntries(vehicles);
      setIsVehiclesLoading(false);

      if (doScrollToBottom) {
        setTimeout(() => {
          const scrollEl = gridContainerRef.current?.closest('.kst-main-content');
          if (scrollEl) {
            scrollEl.scrollTop = scrollEl.scrollHeight;
          }
        }, 0);
      }
    }).catch(() => {
      setIsVehiclesLoading(false);
    });
  }, [getVehicleListPromise]);

  // Load on mount
  useEffect(() => {
    refreshVehicleList();
  }, [refreshVehicleList]);

  const handleButtonCommand = useCallback((functionName: string, checkedItems: any[]) => {
    if (functionName === 'add') {
      setAddModalOpen(true);
    } else if (functionName === 'delete') {
      if (checkedItems.length === 0) {
        App.showPopupNotification('Assigned Vehicle must be selected to delete.', 'warning');
        return;
      }
      const count = checkedItems.length;
      const message = count === 1
        ? 'Are you sure you want to delete this assigned vehicle?'
        : `Are you sure you want to delete ${count} assigned vehicles?`;
      setDeleteConfirmItems(checkedItems);
      setDeleteConfirmMessage(message);
      setDeleteConfirmOpen(true);
    }
  }, []);

  const handleAddSave = useCallback((data: { vehicleName: string }) => {
    const vehicleData = { vehicleName: data.vehicleName };
    setIsSaving(true);
    api.saveCompanyVehicle(vehicleData, () => {
      setIsSaving(false);
      clearCachedValues();
      refreshVehicleList(true);
      setAddModalOpen(false);
    }, () => {
      setIsSaving(false);
      App.showPopupNotification('Failed to save Assigned Vehicle.', 'error');
    });
  }, [clearCachedValues, refreshVehicleList]);

  const handleConfirmDelete = useCallback(() => {
    const idList = deleteConfirmItems.map((elt: any) => elt.id);
    const companyVehicleData = { idList };
    setIsDeleting(true);
    api.deleteCompanyVehicles(companyVehicleData, () => {
      setIsDeleting(false);
      clearCachedValues();
      refreshVehicleList(true);
      setDeleteConfirmOpen(false);
      setDeleteConfirmItems([]);
      setDeleteConfirmMessage('');
    }, () => {
      setIsDeleting(false);
      App.showPopupNotification('Failed to delete Assigned Vehicle.', 'error');
    });
  }, [deleteConfirmItems, clearCachedValues, refreshVehicleList]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmOpen(false);
    setDeleteConfirmItems([]);
    setDeleteConfirmMessage('');
  }, []);

  const handleRowMoved = useCallback((row: any) => {
    setVehicleEntries((prev) => {
      const values = [...(prev ?? [])];
      const idxOfRow = values.findIndex((v: any) => v.id === row.id);
      if (idxOfRow >= 0) {
        values.splice(idxOfRow, 1);
      }
      values.splice(row.listIdx - 1, 0, row);
      for (let i = 0; i < values.length; i++) {
        values[i].listIdx = i + 1;
      }

      // Auto-save after reordering
      const idAndListIdxList = values.map((v: any) => ({ id: v.id, listIdx: v.listIdx }));
      idAndListIdxList.sort((a: any, b: any) => a.listIdx - b.listIdx);
      const idList = idAndListIdxList.map((elt: any) => elt.id);
      const companyVehicleData = { idList };
      api.reorderCompanyVehicles(companyVehicleData, () => {
        clearCachedValues();
      }, () => {
        App.showPopupNotification('Failed to reorder vehicles.', 'error');
      });

      return values;
    });
  }, [clearCachedValues]);

  return (
    <div className={theme}>
      <AddVehicleModal
        open={addModalOpen}
        theme={theme}
        saving={isSaving}
        onSave={handleAddSave}
        onCancel={() => setAddModalOpen(false)}
      />

      <ConfirmModal
        open={deleteConfirmOpen}
        title="Delete Assigned Vehicle"
        message={deleteConfirmMessage}
        confirmText="Delete"
        confirmClass="danger"
        confirmDisabled={isDeleting}
        busy={isDeleting}
        theme={theme}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      <div className="kst-page-container">
        <div className="kst-layout kst-layout--no-sidebar">
          <div className="kst-main-content">
            <div className="kst-page" ref={gridContainerRef}>
              <div className="kst-page-header">
                <h3 className="kst-page-title">Vehicle Management</h3>
                <p className="kst-page-description">
                  Officers can be assigned to vehicles on the Officer Status page. Once an officer is added to a Call, the officer's vehicle will stay the same / not update for that call.
                </p>
              </div>

              <KstDataGrid
                columns={vehicleColumns}
                rows={vehicleRows}
                headerbuttons={vehicleHeaderButtons}
                loading={isVehiclesLoading}
                showcheckbox={true}
                showordercolumn={true}
                mobiletitlefield="vehicleName"
                onButtonCommand={handleButtonCommand}
                onRowMoved={handleRowMoved}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VehicleManagementScreen;

